import React, { useState, useEffect, useCallback } from 'react';
import { apiService } from '../services/api';
import Modal from './Modal';
import StripeCustomerSyncEditor from './StripeCustomerSyncEditor';

interface StripeCustomerIdEditorProps {
  customerId: number;
  currentStripeCustomerId: string | null | undefined;
  customerEmail: string | null | undefined;
  customerName?: string | null | undefined;
  customerContactName?: string | null | undefined;
  customerPhone?: string | null | undefined;
  customerAddress?: string | null | undefined;
  customerStreet?: string | null | undefined;
  customerCity?: string | null | undefined;
  customerPostalCode?: string | null | undefined;
  customerState?: string | null | undefined;
  customerCountry?: string | null | undefined;
  customerTaxId?: string | null | undefined;
  customerVatId?: string | null | undefined;
  customerCurrency?: string | null | undefined;
  customerLanguage?: string | null | undefined;
  companyId?: number;
  allContracts?: any[]; // Contract[] from types
  onUpdated: (newStripeCustomerId: string | null) => void;
}

interface StripeCustomerSearchResult {
  success: boolean;
  found: boolean;
  stripe_customer_id: string | null;
  email: string;
  name: string;
  created?: number;
  metadata?: Record<string, any>;
}

const StripeCustomerIdEditor: React.FC<StripeCustomerIdEditorProps> = ({
  customerId,
  currentStripeCustomerId,
  customerEmail,
  customerName,
  customerContactName,
  customerPhone,
  customerAddress,
  customerStreet,
  customerCity,
  customerPostalCode,
  customerState,
  customerCountry,
  customerTaxId,
  customerVatId,
  customerCurrency,
  customerLanguage,
  companyId: propCompanyId,
  allContracts = [],
  onUpdated,
}) => {
  const [showModal, setShowModal] = useState(false);
  const [showSyncModal, setShowSyncModal] = useState(false);
  const [searchEmail, setSearchEmail] = useState('');
  const [searchResults, setSearchResults] = useState<StripeCustomerSearchResult | null>(null);
  const [similarCustomers, setSimilarCustomers] = useState<StripeCustomerSearchResult[]>([]);
  const [manualStripeId, setManualStripeId] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [searchMode, setSearchMode] = useState<'exact' | 'similar'>('exact');

  // Use the provided companyId (should be resolved by the parent component)
  const companyId = propCompanyId;

  // Initialize with customer email
  useEffect(() => {
    if (customerEmail) {
      setSearchEmail(customerEmail);
    }
    if (currentStripeCustomerId) {
      setManualStripeId(currentStripeCustomerId);
    }
  }, [customerEmail, currentStripeCustomerId]);

  const searchStripeCustomer = useCallback(async () => {
    if (!searchEmail) {
      setError('Please enter an email address');
      return;
    }

    setIsLoading(true);
    setError(null);
    setSearchResults(null);
    setSimilarCustomers([]);

    try {
      if (searchMode === 'exact') {
        const response = await apiService.customers.getStripeCustomersByEmail(searchEmail, companyId);
        setSearchResults(response);
      } else {
        // Search for similar emails (same domain or similar usernames)
        await searchSimilarCustomers();
      }
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : String(err);
      setError(`Failed to search Stripe customers. Please check your Stripe credentials. ${errorMessage}`);
      console.error('Search error:', err);
    } finally {
      setIsLoading(false);
    }
  }, [searchEmail, companyId, searchMode]);

  const searchSimilarCustomers = async () => {
    if (!searchEmail) return;

    try {
      // Extract domain from email
      const emailParts = searchEmail.split('@');
      const domain = emailParts.length > 1 ? emailParts[1] : '';
      const username = emailParts[0];

      // List all customers and filter for similar ones
      const response = await apiService.customers.listStripeCustomers(companyId, 100);
      
      if (response && response.customers) {
        const filtered = response.customers.filter((cust: any) => {
          // Match same domain or similar username (contains same substring)
          if (!cust.email) return false;
          
          const custEmailParts = cust.email.split('@');
          const custDomain = custEmailParts.length > 1 ? custEmailParts[1] : '';
          const custUsername = custEmailParts[0];

          // Same domain
          if (domain && custDomain && custDomain.toLowerCase() === domain.toLowerCase()) {
            return true;
          }
          
          // Similar username (contains same substring of at least 4 chars)
          if (username.length >= 4 && custUsername.toLowerCase().includes(username.toLowerCase().substring(0, 4))) {
            return true;
          }
          
          return false;
        });
        
        // Convert to search result format
        const results: StripeCustomerSearchResult[] = filtered.map((cust: any) => ({
          success: true,
          found: true,
          stripe_customer_id: cust.id,
          email: cust.email,
          name: cust.name || '',
          created: cust.created,
          metadata: cust.metadata,
        }));
        
        setSimilarCustomers(results);
      }
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : String(err);
      setError(`Failed to search for similar customers: ${errorMessage}`);
      console.error('Similar search error:', err);
    }
  };

  const handleAssignExisting = async (stripeCustomerId: string) => {
    if (!stripeCustomerId) return;

    setIsLoading(true);
    setError(null);

    try {
      const response = await apiService.customers.assignStripeCustomerId(customerId, stripeCustomerId);
      setSuccessMessage(`Successfully assigned Stripe customer: ${stripeCustomerId}`);
      onUpdated(stripeCustomerId);
      
      // Close modal after a short delay
      setTimeout(() => {
        setShowModal(false);
        setSuccessMessage(null);
      }, 1500);
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : String(err);
      setError(`Failed to assign Stripe customer: ${errorMessage}`);
      console.error('Assign error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateStripeCustomer = async () => {
    if (!searchEmail) {
      setError('Please enter an email address to create a Stripe customer');
      return;
    }

    setIsLoading(true);
    setError(null);

    // Use customer data from props if available, otherwise use search email
    // Convert null to undefined for API compatibility (TypeScript strict null checks)
    const name = (customerName ?? undefined) || searchEmail;
    const contactName = customerContactName ?? undefined;
    const phone = customerPhone ?? undefined;
    const currency = customerCurrency ?? undefined;
    const language = customerLanguage ?? undefined;
    const taxId = customerTaxId ?? undefined;
    const vatId = customerVatId ?? undefined;
    const customerAddressSafe = customerAddress ?? undefined;
    const customerCitySafe = customerCity ?? undefined;
    const customerCountrySafe = customerCountry ?? undefined;
    const customerPostalCodeSafe = customerPostalCode ?? undefined;
    const customerStateSafe = customerState ?? undefined;
    const customerStreetSafe = customerStreet ?? undefined;

    // Build structured address from individual components if available
    // Stripe address format: line1, city, state, postal_code, country
    const stripeAddress = customerStreetSafe || customerCitySafe || customerPostalCodeSafe || customerCountrySafe || customerStateSafe ? {
      line1: customerStreetSafe || '',
      city: customerCitySafe || '',
      state: customerStateSafe || '',
      postal_code: customerPostalCodeSafe || '',
      country: customerCountrySafe || '',
    } : undefined;

    // Build metadata with all available customer information
    const metadata: Record<string, string> = {};
    if (customerId) {
      metadata.local_customer_id = String(customerId);
    }
    if (customerName) {
      metadata.customer_name = customerName;
    }
    if (customerContactName) {
      metadata.contact_name = customerContactName;
    }
    if (customerPhone) {
      metadata.phone = customerPhone;
    }
    if (customerAddress) {
      metadata.address_full = customerAddress;
    }
    if (customerTaxId) {
      metadata.tax_id = customerTaxId;
    }
    if (customerVatId) {
      metadata.vat_id = customerVatId;
    }
    if (customerCurrency) {
      metadata.currency = customerCurrency;
    }
    if (customerLanguage) {
      metadata.language = customerLanguage;
    }

    try {
      // Note: The API endpoint accepts customer details in the request body
      // which get passed to Stripe. This ensures all customer info is stored with the Stripe customer.
      const response = await apiService.customers.createStripeCustomer(
        searchEmail,
        name,
        companyId,
        // Use safe variables that are already converted from null to undefined
        customerAddressSafe,
        vatId,
        contactName,
        phone,
        currency,
        language,
        taxId,
        customerCitySafe,
        customerCountrySafe,
        customerPostalCodeSafe,
        customerStateSafe,
        customerStreetSafe
      );
      if (response && response.stripe_customer_id) {
        setSuccessMessage(`Successfully created Stripe customer: ${response.stripe_customer_id} with full customer details.`);
        // Assign the newly created customer to the local customer
        await handleAssignExisting(response.stripe_customer_id);
      } else {
        setError('Failed to create Stripe customer. Please try again.');
      }
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : String(err);
      setError(`Failed to create Stripe customer: ${errorMessage}`);
      console.error('Create error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateAndLinkStripeCustomer = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await apiService.customers.createOrLinkStripeCustomer(
        customerId,
        companyId
      );
      if (response && response.success) {
        if (response.exists) {
          setSuccessMessage(`Stripe customer already exists: ${response.stripe_customer_id}`);
        } else {
          setSuccessMessage(`Successfully created and linked Stripe customer: ${response.stripe_customer_id}`);
        }
        onUpdated(response.stripe_customer_id);
        
        // Close modal after a short delay
        setTimeout(() => {
          setShowModal(false);
          setSuccessMessage(null);
        }, 1500);
      } else {
        setError('Failed to create or link Stripe customer. Please try again.');
      }
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : String(err);
      setError(`Failed to create or link Stripe customer: ${errorMessage}`);
      console.error('Create and link error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateUserFromCustomer = async () => {
    if (!searchEmail) {
      setError('Please enter an email address');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Generate a username from the email (part before @)
      const username = searchEmail.split('@')[0] || searchEmail;
      
      // Generate a safe password - bcrypt has a 72-byte limit
      // Using a 12-character alphanumeric password is safe (12 bytes)
      const generateSafePassword = () => {
        const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
        let password = '';
        for (let i = 0; i < 12; i++) {
          password += chars.charAt(Math.floor(Math.random() * chars.length));
        }
        return password;
      };
      
      const password = generateSafePassword();
      
      // Build user data from customer details
      const userData: any = {
        username: username,
        email: searchEmail,
        password: password,
        first_name: customerName || '',
        // Use customer address as user address if available
        address: customerAddress || '',
        // Use VAT ID as tax_id if available
        tax_id: customerVatId || '',
        is_active: true,
        access: 'user',
        preferred_language: 'en',
        // Link to customer if customerId is provided
        ...(customerId && { customer_id: customerId }),
      };

      const response = await apiService.users.createUser(userData);
      if (response && response.id) {
        setSuccessMessage(`Successfully created user: ${response.username} (ID: ${response.id}). Linked to customer ${customerId || 'N/A'}. Password: ${password}`);
        // Don't auto-close, let the user see the success and password
      } else {
        setError('Failed to create user. Please try again.');
      }
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : String(err);
      // If the error is about password length, retry with a shorter password
      if (errorMessage.includes('password') && errorMessage.includes('72')) {
        try {
          const username = searchEmail.split('@')[0] || searchEmail;
          // Use a very short password as fallback
          const userData: any = {
            username: username,
            email: searchEmail,
            password: 'temp123',
            first_name: customerName || '',
            address: customerAddress || '',
            tax_id: customerVatId || '',
            is_active: true,
            access: 'user',
            preferred_language: 'en',
            ...(customerId && { customer_id: customerId }),
          };
          const response = await apiService.users.createUser(userData);
          if (response && response.id) {
            setSuccessMessage(`Successfully created user: ${response.username} (ID: ${response.id}). Linked to customer ${customerId || 'N/A'}. Password: temp123`);
            return;
          }
        } catch (retryErr: unknown) {
          const retryErrorMessage = retryErr instanceof Error ? retryErr.message : String(retryErr);
          setError(`Failed to create user: ${retryErrorMessage}`);
          console.error('Create user retry error:', retryErr);
          return;
        }
      }
      setError(`Failed to create user: ${errorMessage}`);
      console.error('Create user error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAssignManual = async () => {
    if (!manualStripeId || !manualStripeId.startsWith('cus_')) {
      setError('Please enter a valid Stripe customer ID (should start with cus_)');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await apiService.customers.assignStripeCustomerId(customerId, manualStripeId);
      setSuccessMessage(`Successfully assigned Stripe customer: ${manualStripeId}`);
      onUpdated(manualStripeId);
      
      // Close modal after a short delay
      setTimeout(() => {
        setShowModal(false);
        setSuccessMessage(null);
      }, 1500);
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : String(err);
      setError(`Failed to assign Stripe customer: ${errorMessage}`);
      console.error('Assign error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRemove = async () => {
    setIsLoading(true);
    setError(null);

    try {
      // Clear the stripe_customer_id by assigning empty string
      const response = await apiService.customers.assignStripeCustomerId(customerId, '');
      setSuccessMessage('Stripe customer ID removed');
      onUpdated(null);
      
      setTimeout(() => {
        setShowModal(false);
        setSuccessMessage(null);
      }, 1500);
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : String(err);
      setError(`Failed to remove Stripe customer ID: ${errorMessage}`);
      console.error('Remove error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <button 
        className="btn btn-outline btn-sm"
        onClick={() => setShowModal(true)}
        title="Edit Stripe Customer ID"
      >
        Edit
      </button>

      {showModal && (
        <Modal
          title="Edit Stripe Customer ID"
          onClose={() => setShowModal(false)}
          size="lg"
          minHeight={650}
        >
          <div style={{ padding: '20px', minWidth: '600px', height: '100%' }}>
            {error && (
              <div className="alert alert-error" style={{ marginBottom: '15px' }}>
                {error}
              </div>
            )}
            
            {successMessage && (
              <div className="alert alert-success" style={{ marginBottom: '15px' }}>
                {successMessage}
              </div>
            )}

            <div className="form-group">
              <label className="form-label">Current Stripe Customer ID</label>
              <div style={{ 
                padding: '10px', 
                background: '#f8f9fa', 
                borderRadius: '4px',
                border: '1px solid #dee2e6',
                marginBottom: '15px',
                width: '100%'
              }}>
                <code style={{ color: '#495057' }}>
                  {currentStripeCustomerId || 'Not assigned'}
                </code>
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">
                Search Stripe Customers by Email
                <span style={{ fontSize: '0.85em', color: '#6c757d', marginLeft: '5px' }}>
                  (Recommended to prevent duplicates)
                </span>
              </label>
              <div style={{ display: 'flex', gap: '10px', width: '100%', marginBottom: '10px' }}>
                <input
                  type="email"
                  value={searchEmail}
                  onChange={(e) => setSearchEmail(e.target.value)}
                  className="form-control"
                  placeholder="Enter email to search in Stripe"
                  style={{ flex: 1 }}
                />
                <button 
                  type="button"
                  className="btn btn-primary"
                  onClick={searchStripeCustomer}
                  disabled={isLoading || !searchEmail}
                  style={{ flexShrink: 0 }}
                >
                  {isLoading ? 'Searching...' : 'Search'}
                </button>
              </div>
              <div style={{ display: 'flex', gap: '10px', marginBottom: '15px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '5px', cursor: 'pointer' }}>
                  <input
                    type="radio"
                    name="searchMode"
                    checked={searchMode === 'exact'}
                    onChange={() => setSearchMode('exact')}
                    disabled={isLoading}
                  />
                  <span>Exact match</span>
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '5px', cursor: 'pointer' }}>
                  <input
                    type="radio"
                    name="searchMode"
                    checked={searchMode === 'similar'}
                    onChange={() => setSearchMode('similar')}
                    disabled={isLoading}
                  />
                  <span>Similar emails/domains</span>
                </label>
              </div>
            </div>

            {searchResults && searchResults.found && (
              <div className="form-group">
                <label className="form-label">Search Results</label>
                <div style={{ 
                  padding: '15px', 
                  background: '#e7f3ff', 
                  borderRadius: '4px',
                  border: '1px solid #b3d9ff',
                  marginBottom: '15px',
                  width: '100%'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 'bold', marginBottom: '5px' }}>
                        Found Stripe Customer
                      </div>
                      <div><strong>ID:</strong> {searchResults.stripe_customer_id}</div>
                      <div><strong>Email:</strong> {searchResults.email}</div>
                      <div><strong>Name:</strong> {searchResults.name || 'N/A'}</div>
                      {searchResults.created && (
                        <div><strong>Created:</strong> {new Date(searchResults.created * 1000).toLocaleDateString()}</div>
                      )}
                    </div>
                    <button 
                      type="button"
                      className="btn btn-primary btn-sm"
                      onClick={() => handleAssignExisting(searchResults.stripe_customer_id!)}
                      disabled={isLoading}
                      style={{ marginLeft: '15px', flexShrink: 0 }}
                    >
                      Assign This Customer
                    </button>
                  </div>
                </div>
              </div>
            )}

            {similarCustomers.length > 0 && (
              <div className="form-group">
                <label className="form-label">
                  Similar Customers ({similarCustomers.length} found with same domain or similar email)
                </label>
                <div style={{ 
                  marginBottom: '15px',
                  maxHeight: '300px',
                  overflowY: 'auto',
                  border: '1px solid #dee2e6',
                  borderRadius: '4px',
                  background: '#fff'
                }}>
                  {similarCustomers.map((cust, index) => (
                    <div 
                      key={cust.stripe_customer_id || index}
                      style={{ 
                        padding: '12px 15px', 
                        borderBottom: index < similarCustomers.length - 1 ? '1px solid #e9ecef' : 'none',
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'center'
                      }}
                    >
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: '500', color: '#495057' }}>
                          {cust.email}
                        </div>
                        {cust.name && (
                          <div style={{ fontSize: '0.85em', color: '#6c757d' }}>
                            {cust.name}
                          </div>
                        )}
                        <div style={{ fontSize: '0.8em', color: '#adb5bd' }}>
                          {cust.stripe_customer_id}
                        </div>
                      </div>
                      <button 
                        type="button"
                        className="btn btn-primary btn-sm"
                        onClick={() => handleAssignExisting(cust.stripe_customer_id!)}
                        disabled={isLoading}
                        style={{ flexShrink: 0, marginLeft: '10px' }}
                      >
                        Assign
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {searchResults && !searchResults.found && similarCustomers.length === 0 && (
              <div className="alert alert-info" style={{ marginBottom: '15px' }}>
                No existing Stripe customer found with email: {searchResults.email}
                <div style={{ marginTop: '10px' }}>
                  <button 
                    type="button"
                    className="btn btn-success btn-sm"
                    onClick={handleCreateStripeCustomer}
                    disabled={isLoading}
                    style={{ marginRight: '10px' }}
                  >
                    Create & Assign Stripe Customer
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary btn-sm"
                    onClick={handleCreateAndLinkStripeCustomer}
                    disabled={isLoading}
                    style={{ marginRight: '10px' }}
                  >
                    Create & Link Automatically
                  </button>
                  <button
                    type="button"
                    className="btn btn-info btn-sm"
                    onClick={handleCreateUserFromCustomer}
                    disabled={isLoading}
                    title="Create a system user based on customer details"
                  >
                    Create User from Customer
                  </button>
                </div>
              </div>
            )}

            <hr style={{ margin: '25px 0', border: 'none', borderTop: '1px solid #dee2e6' }} />
            
            {/* Create System User - Always available when email is entered */}
            {searchEmail && (
              <div className="form-group">
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '15px' }}>
                  <button
                    type="button"
                    className="btn btn-info btn-sm"
                    onClick={handleCreateUserFromCustomer}
                    disabled={isLoading}
                    title="Create a system user based on customer details"
                  >
                    Create System User
                  </button>
                </div>
              </div>
            )}
            
            <hr style={{ margin: '25px 0', border: 'none', borderTop: '1px solid #dee2e6' }} />

            <div className="form-group">
              <label className="form-label">
                Or Enter Stripe Customer ID Manually
              </label>
              <div style={{ display: 'flex', gap: '10px', width: '100%' }}>
                <input
                  type="text"
                  value={manualStripeId}
                  onChange={(e) => setManualStripeId(e.target.value)}
                  className="form-control"
                  placeholder="cus_xxxxxxxxxxxxx"
                  style={{ fontFamily: 'monospace', flex: 1 }}
                />
                <button 
                  type="button"
                  className="btn btn-secondary"
                  onClick={handleAssignManual}
                  disabled={isLoading || !manualStripeId || !manualStripeId.startsWith('cus_')}
                  style={{ flexShrink: 0 }}
                >
                  Assign Manually
                </button>
              </div>
              <small style={{ color: '#6c757d', display: 'block', marginTop: '5px' }}>
                Must start with 'cus_'
              </small>
            </div>

            {currentStripeCustomerId && (
              <>
                <hr style={{ margin: '25px 0', border: 'none', borderTop: '1px solid #dee2e6' }} />
                <div className="form-group">
                  <div style={{ display: 'flex', gap: '10px', marginBottom: '15px' }}>
                    <button 
                      type="button"
                      className="btn btn-primary btn-sm"
                      onClick={() => setShowSyncModal(true)}
                      disabled={isLoading}
                      style={{ flex: 1 }}
                    >
                      Sync Data with Stripe
                    </button>
                    <button 
                      type="button"
                      className="btn btn-danger btn-sm"
                      onClick={handleRemove}
                      disabled={isLoading}
                      style={{ flex: 1 }}
                    >
                      Remove Stripe Customer ID
                    </button>
                  </div>
                  <small style={{ color: '#6c757d', display: 'block' }}>
                    Sync: Compare and update Stripe customer data with system customer data
                  </small>
                  <small style={{ color: '#6c757d', display: 'block', marginTop: '5px' }}>
                    Remove: This will clear the Stripe customer ID from this customer. 
                    The customer will get a new one automatically when needed.
                  </small>
                </div>
              </>
            )}

            <div className="modal-footer" style={{ marginTop: '25px' }}>
              <button 
                type="button" 
                className="btn btn-secondary" 
                onClick={() => setShowModal(false)}
                disabled={isLoading}
              >
                Close
              </button>
            </div>
          </div>
        </Modal>
      )}
      
      {/* Sync Modal */}
      {showSyncModal && (
        <StripeCustomerSyncEditor
          customerId={customerId}
          currentStripeCustomerId={currentStripeCustomerId || null}
          companyId={companyId}
          onUpdated={(newId) => {
            onUpdated(newId);
            setShowSyncModal(false);
          }}
          onClose={() => setShowSyncModal(false)}
          onError={setError}
          onSuccess={setSuccessMessage}
        />
      )}
    </>
  );
};

export default StripeCustomerIdEditor;
